import Utils from 'core-helpers/utils.js';
import AdjustService from 'core-services/tracking/adjust-service.js';
import GTM from 'tracking/tracking-gtm.js';
import Optimizely from 'tracking/tracking-optimizely.js';
import Snowplow from 'tracking/tracking-snowplow.js';
import TrackingEventsConstants from 'tracking/tracking-constants.js';
import SharedDataService from 'data/shared-data-service.js';
import CurrentUser from 'user/current-user-service.js';
import ExceptionService from 'core-services/exceptions/exception-service.js';
import QueryParameters from 'helpers/query-parameters.js';

// Vars
const filename = 'tracking-controller.js';
let currentUserID = null;

const trackingProviders = [
    { name: 'GTM', provider: GTM, logs: false },
    { name: 'Optimizely', provider: Optimizely, logs: false },
    { name: 'Snowplow', provider: Snowplow, logs: false },
    { name: 'Adjust', provider: AdjustService, logs: false }
];

/**
 * @typedef {Object} EventTemplate
 * @property {String} real_event_name
 * @property {Object} [adjust]
 * @property {String} [adjust.token]
 * @property {String} [adjust.type]
 * @property {String[]} [exclusions]
 */

/**
 * Returns an event template from an event name (alias)
 * @param  {String} eventName
 * @return {EventTemplate}
 * @private
 */
const getEventTemplate = (eventName) => {
    const eventTemplate = TrackingEventsConstants[eventName];
    if (!eventTemplate || !eventTemplate.real_event_name) {
        throw new Error(`[File: ${filename}] Event "${eventName}" is not found.`);
    }
    return eventTemplate;
};

/**
 * Extends the event parameters with common params.
 * @param  {Object} params
 * @return {Object}
 * @private
 */
const extendWithCommonData = (params) => {
    return {
        ...Utils.deepClone(params),
        ...Utils.deepClone(QueryParameters.getInstitutionParameters())
    };
};

/**
 * Checks if we can send the event to the tracking service
 * @param {EventTemplate} eventTemplate
 * @param {String} trackingServiceName
 */
const canSendEvent = (eventTemplate, trackingServiceName) => {
    if (eventTemplate.exclusions && Utils.isArray(eventTemplate.exclusions)) {
        const isExcluded = eventTemplate.exclusions.includes(trackingServiceName);
        return !isExcluded;
    }
    return true;
};

/**
 * Send an event to all tracking provider
 * @param  {String} eventName
 * @param  {Object} data
 * @private
 */
const sendEvent = (eventName, data) => {
    const eventTemplate = getEventTemplate(eventName);
    const params = extendWithCommonData(data);

    trackingProviders.forEach(({ name, provider: { sendEvent } }) => {
        if (sendEvent && canSendEvent(eventTemplate, name)) {
            sendEvent(eventTemplate, params);
        }
    });
};

/**
 * When user is recognised, we add extra data to trackers.
 * We add a logic to avoid multiple authentications
 * based on the current user id. In some cases this ID can change
 * and a new user needs to be identified. (e.g.: registration)
 * @param {Object} user
 * @private
 */
const identifyUser = (user) => {
    if (currentUserID === user.uid) {
        return false;
    }

    // Add Sentry user context
    ExceptionService.identifyUser(user);

    trackingProviders.forEach(({ provider }) => {
        if (provider.identifyUser) {
            provider.identifyUser(user);
        }

        if (provider.identifyOptimizelyVisitor) {
            provider.identifyOptimizelyVisitor(Optimizely.getVisitorId());
        }
    });

    // Store userID to avoid multiple authentications
    currentUserID = user.uid;
};

/**
 * Middleware wrapper before sending events.
 * This function tries to get some information
 * about the user first and then send the event,
 * so we can ensure that we have user information
 * along with the event when it's available.
 * @param {String} eventName
 * @param {Object} data
 * @public
 */
const send = (eventName, data = {}) => {
    const onComplete = () => {
        sendEvent(eventName, data);
    };

    CurrentUser.get()
        .then(identifyUser)
        .catch(() => {})
        .then(onComplete);
};

/**
 * Shortcut handler for send() that wraps a promise
 * that always resolves. It ensures the tracking has been
 * sent using a delay timeout to let enough time
 * for the tracking request to process.
 * @return {Promise}
 * @public
 */
const sendAndWait = (eventName, data) => {
    send(eventName, data);
    return new Promise((resolve) => {
        window.setTimeout(resolve, 750);
    });
};

const init = () => {
    /**
     * Everytime the app load, we store a
     * marketing data object in the session storage.
     */
    SharedDataService.storeMarketingData();

    trackingProviders.forEach(({ provider: { load }, logs }) => {
        if (load) {
            load({ logs });
        }
    });
};

init();

const TrackingController = {
    send,
    sendAndWait
};

export default TrackingController;
